import proj4 from 'proj4';

/**
 * Sistemas de referencia usados por la aplicacion.
 * - 4326 WGS84 geografico (MapLibre, permalinks).
 * - 3857 Web Mercator (teselas).
 * - 4258 ETRS89 geografico.
 * - 25829/25830/25831 ETRS89 UTM husos 29/30/31 (Catastro, IGN).
 * - 4083 REGCAN95 UTM 28 (Canarias).
 * Todas las transformaciones pasan por proj4 con estas definiciones.
 */
export const DEFINICIONES: Record<number, string> = {
  4326: '+proj=longlat +datum=WGS84 +no_defs',
  3857: '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +wktext +no_defs',
  4258: '+proj=longlat +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +no_defs',
  25829: '+proj=utm +zone=29 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  25830: '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  25831: '+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
  4083: '+proj=utm +zone=28 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs',
};

for (const [codigo, def] of Object.entries(DEFINICIONES)) {
  proj4.defs(`EPSG:${codigo}`, def);
}

export const HUSOS_UTM: ReadonlyArray<number> = [25829, 25830, 25831, 4083];

/** Nombre corto para la interfaz. */
export function nombreEpsg(epsg: number): string {
  switch (epsg) {
    case 25829: return 'ETRS89 UTM 29N';
    case 25830: return 'ETRS89 UTM 30N';
    case 25831: return 'ETRS89 UTM 31N';
    case 4083: return 'REGCAN95 UTM 28N';
    case 4326: return 'WGS84';
    case 3857: return 'Web Mercator';
    default: return `EPSG:${epsg}`;
  }
}

/**
 * Huso UTM oficial para un punto de Espana. Canarias va en REGCAN95 (4083);
 * la peninsula y Baleares por longitud: oeste de -6 huso 29, entre -6 y 0 huso 30,
 * este de 0 huso 31. Es la misma regla que ofrecen los menus contextuales del plugin.
 */
export function husoParaLonLat(lon: number, lat: number): number {
  if (lat < 29.7 && lon < -13) return 4083;
  if (lon < -6) return 25829;
  if (lon < 0) return 25830;
  return 25831;
}

/** Numero de zona UTM (28, 29, 30, 31) de un codigo EPSG de esta aplicacion. */
export function zonaDeEpsg(epsg: number): number {
  if (epsg === 4083) return 28;
  if (epsg >= 25828 && epsg <= 25838) return epsg - 25800;
  throw new Error(`EPSG ${epsg} no es un huso UTM conocido`);
}

export function proyectar(deEpsg: number, aEpsg: number, punto: [number, number]): [number, number] {
  if (deEpsg === aEpsg) return [punto[0], punto[1]];
  const r = proj4(`EPSG:${deEpsg}`, `EPSG:${aEpsg}`, [punto[0], punto[1]]);
  return [r[0], r[1]];
}

export function aUtm(lon: number, lat: number, epsg?: number): { x: number; y: number; epsg: number } {
  const e = epsg ?? husoParaLonLat(lon, lat);
  const [x, y] = proyectar(4326, e, [lon, lat]);
  return { x, y, epsg: e };
}

export function aLonLat(x: number, y: number, epsg: number): [number, number] {
  return proyectar(epsg, 4326, [x, y]);
}

/** Proyecta un anillo (lista de vertices) entre dos sistemas. */
export function proyectarAnillo(deEpsg: number, aEpsg: number, anillo: [number, number][]): [number, number][] {
  if (deEpsg === aEpsg) return anillo;
  return anillo.map((p) => proyectar(deEpsg, aEpsg, p));
}

/** Bbox [minx, miny, maxx, maxy] de un sistema a otro, muestreando los bordes para no perder curvatura. */
export function proyectarBbox(deEpsg: number, aEpsg: number, bbox: [number, number, number, number]): [number, number, number, number] {
  if (deEpsg === aEpsg) return [...bbox];
  const [minx, miny, maxx, maxy] = bbox;
  const n = 8;
  let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const candidatos: [number, number][] = [
      [minx + (maxx - minx) * t, miny],
      [minx + (maxx - minx) * t, maxy],
      [minx, miny + (maxy - miny) * t],
      [maxx, miny + (maxy - miny) * t],
    ];
    for (const p of candidatos) {
      const [x, y] = proyectar(deEpsg, aEpsg, p);
      if (x < a) a = x;
      if (y < b) b = y;
      if (x > c) c = x;
      if (y > d) d = y;
    }
  }
  return [a, b, c, d];
}

/** Formatea coordenadas para la barra inferior: "440.300 E 4.474.300 N (ETRS89 UTM 30N)". */
export function formatearUtm(x: number, y: number, epsg: number): string {
  const f = (v: number) => Math.round(v).toLocaleString('es-ES');
  return `${f(x)} E ${f(y)} N (${nombreEpsg(epsg)})`;
}
